import { getVisibleInferenceModels, NotificationType, useApp, useTranslation } from "@next-platform/client-common-core";
import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";

import { ROUTES } from "@/constants";
import { useInferenceSettings } from "@/hooks";
import useToast from "@/hooks/useToast";
import useAuth from "@/services/useAuth";

interface IUseModelSelectionProps {
  setIsOpen: (isOpen: boolean) => void;
  isOpen: boolean;
  onModelChange?: (modelId: string) => void;
  currentModelId?: string;
}

const useModelSelection = ({ setIsOpen, isOpen, currentModelId }: IUseModelSelectionProps) => {
  const { permissions } = useAuth();
  const { inferenceModels } = useApp();
  const navigate = useNavigate();
  const { displayToast } = useToast();
  const { t } = useTranslation();
  const { inferenceModel: reduxInferenceModel, updateInferenceModel } = useInferenceSettings();

  // Use provided currentModelId (staged state) or fall back to Redux state
  const inferenceModel = currentModelId ?? reduxInferenceModel;

  const [selectedModelId, setSelectedModelId] = useState(inferenceModel);
  const [disabledKeys, setDisabledKeys] = useState<string[]>([]);
  const isDisabledModelSelected = disabledKeys.includes(selectedModelId);
  const isSetModelDisabled = inferenceModel === selectedModelId;

  const modelsToDisplay = useMemo(
    () =>
      getVisibleInferenceModels(inferenceModels, permissions).sort((left, right) => {
        if (left.id === inferenceModel) return -1;
        if (right.id === inferenceModel) return 1;
        return 0;
      }),
    [inferenceModels, permissions, inferenceModel],
  );

  useEffect(() => {
    const disabledKeys: string[] = [];
    modelsToDisplay.forEach((model) => {
      if (model.permission && !permissions?.includes(model.permission)) {
        disabledKeys.push(model.id);
      }
    });
    setDisabledKeys(disabledKeys);
  }, [modelsToDisplay, permissions]);

  useEffect(() => {
    setSelectedModelId(inferenceModel);
  }, [inferenceModel, isOpen]);

  const changeModel = (model: string) => {
    setSelectedModelId(model);
  };

  const handleModelConfirmation = () => {
    if (isDisabledModelSelected) {
      navigate(ROUTES.subscribe);
      return;
    }

    //No longer participates in generation settings updates
    updateInferenceModel(selectedModelId);
    const selectedModelName = modelsToDisplay.find((model) => model.id === selectedModelId)?.name;
    displayToast({
      message: t("chat:page.header.modelSelection.dropdown.switchModel.toast", { modelName: selectedModelName }),
      type: NotificationType.success,
    });

    setIsOpen(false);
  };

  return {
    modelsToDisplay,
    selectedModelId,
    disabledKeys,
    isDisabledModelSelected,
    changeModel,
    handleModelConfirmation,
    isSetModelDisabled,
  };
};

export default useModelSelection;
